import { Button } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { type CustomElement, customElementsApi } from '@/api/customElements';
import type {
  FileEntryCondition,
  FormSection,
  WorkflowFieldPropertiesPayload,
  WorkflowStep,
} from '@/api/workflows';
import { PageHeader } from '@/components/PageHeader';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';

import { CustomStepDetailsModal } from '../form-builder/customElements/CustomStepDetailsModal';
import { createInMemoryFormEditorDataSource } from '../form-builder/customElements/inMemoryFormEditorDataSource';
import { sectionToPayload } from '../form-builder/customElements/toCustomPayload';
import { DynamicFormEditorShell } from '../form-builder/DynamicFormEditorShell';
import { FieldPropertiesPanel } from '../form-builder/FieldPropertiesPanel';

import { NameFields } from './NameFields';

/**
 * Edits a saved custom element. Dispatches by `.type`:
 *  - Field: `FieldPropertiesPanel` alone, autosaving exactly like the live workflow
 *    builder (its visibility-conditions UI auto-hides since `stepId` is undefined).
 *  - Section: the full 3-panel `DynamicFormEditorShell`, pinned to this one section,
 *    backed by an in-memory data source — structural edits are instant/local, an
 *    explicit Save button persists the whole section (+ library name) at once.
 *  - Step: same shell, unrestricted, plus a "Step details" button beside Preview for
 *    the step's own title/description. Same explicit-Save model as Section.
 */
export function CustomElementEditorPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [element, setElement] = useState<CustomElement | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!id) return;
    customElementsApi
      .get(id)
      .then(setElement)
      .catch(() => setLoadFailed(true));
  }, [id]);

  const goBack = useCallback(() => navigate('/originator/custom-elements'), [navigate]);

  if (loadFailed) {
    return (
      <div style={{ padding: 24 }}>
        <PageHeader title={t('workflow.customElements.selectorTitle')} onBack={goBack} />
        <p style={{ marginTop: 16, color: '#dc2626' }}>
          {t('workflow.customElements.editLoadFailed')}
        </p>
      </div>
    );
  }

  if (!element || !id) {
    return <div style={{ minHeight: 320, background: '#f5f7fa' }} />;
  }

  if (element.type === 'Field') {
    return <FieldElementEditor key={id} id={id} element={element} onBack={goBack} />;
  }
  if (element.type === 'Section') {
    return <SectionElementEditor key={id} id={id} element={element} onBack={goBack} />;
  }
  return <StepElementEditor key={id} id={id} element={element} onBack={goBack} />;
}

function FieldElementEditor({
  id,
  element,
  onBack,
}: {
  id: string;
  element: CustomElement;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const [nameEn, setNameEn] = useState(element.nameEn);
  const [nameFr, setNameFr] = useState(element.nameFr);
  const [field, setField] = useState(element.field!);
  const [savingName, setSavingName] = useState(false);

  // Takes the panel's onSave shape verbatim — `properties` must be the FRESH
  // bag built by the panel (buildProperties), not the stale `field` object.
  // A previous version merged `{ ...field, ...payload }` and sent the merged
  // field as `properties`, which left the new values nested under an ignored
  // `properties` key and silently persisted the OLD flat values instead.
  const persist = useCallback(
    async (
      nextNameEn: string,
      nextNameFr: string,
      payload: {
        labelEn: string;
        labelFr: string | null;
        required: boolean;
        properties: WorkflowFieldPropertiesPayload;
      },
    ) => {
      const updated = await customElementsApi.updateField(id, nextNameEn, nextNameFr, {
        labelEn: payload.labelEn,
        labelFr: payload.labelFr,
        required: payload.required,
        properties: payload.properties,
      });
      // Pin the field's client-side id across saves. The backend mints a fresh
      // Guid for the field payload on every updateField, but FieldPropertiesPanel
      // keys its editor on field.id — so adopting the new id would remount the
      // panel mid-edit and blow away in-flight keystrokes (the autosave-eats-my-
      // typing bug). The field id is a purely local React identity here (it's
      // regenerated server-side each save regardless), so keeping it stable is safe.
      setField((prev) => ({ ...updated.field!, id: prev.id }));
    },
    [id],
  );

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
      <PageHeader title={t('workflow.customElements.editField')} onBack={onBack} />
      <NameFields
        nameEn={nameEn}
        nameFr={nameFr}
        onChangeNameEn={setNameEn}
        onChangeNameFr={setNameFr}
        saving={savingName}
        onSave={async () => {
          setSavingName(true);
          try {
            // Renaming re-sends the field unchanged; WorkflowField's flat shape
            // doubles as the properties bag on the wire.
            await persist(nameEn, nameFr, {
              labelEn: field.labelEn,
              labelFr: field.labelFr,
              required: field.required,
              properties: field,
            });
            void message.success(t('workflow.customElements.saved'));
          } catch (err) {
            handleApiError(err, t, t('workflow.customElements.saveFailed'));
          } finally {
            setSavingName(false);
          }
        }}
      />
      <style>{`.fep-standalone aside { border-left: none; }`}</style>
      <div
        className="fep-standalone"
        style={{
          width: 'fit-content',
          marginTop: 16,
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        <FieldPropertiesPanel
          field={field}
          templateId={undefined}
          stepId={undefined}
          location={null}
          allSteps={[]}
          onSave={async (payload) => {
            await persist(nameEn, nameFr, { ...payload, labelFr: payload.labelFr ?? null });
          }}
          onRemove={async () => onBack()}
          onConditionsChanged={async () => {}}
          onClose={onBack}
          hideSaveAsCustom
        />
      </div>
    </div>
  );
}

function SectionElementEditor({
  id,
  element,
  onBack,
}: {
  id: string;
  element: CustomElement;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const [nameEn, setNameEn] = useState(element.nameEn);
  const [nameFr, setNameFr] = useState(element.nameFr);
  const [sections, setSections] = useState<FormSection[]>([element.section!]);
  const [saving, setSaving] = useState(false);
  // Use the custom element's own (route-stable) id as the synthetic step id —
  // the backend mints a fresh id for element.section on every save, so that id
  // can't be relied on across a save/reload cycle, but this one never changes.
  const sectionStepId = id;

  const dataSource = useMemo(() => createInMemoryFormEditorDataSource(setSections), []);

  // Wrap the single section in a synthetic step so the conditions picker can
  // find eligible source fields (RadioGroup, CheckboxGroup, etc.) within the section.
  const syntheticAllSteps = useMemo(
    (): WorkflowStep[] => [
      {
        id: sectionStepId,
        type: 'DynamicForm',
        titleEn: nameEn,
        titleFr: nameFr,
        descriptionEn: '',
        descriptionFr: '',
        fileEntries: [],
        additionalDocumentEntries: [],
        optionalDocumentExtensions: [],
        optionalDocumentMaxFileSizeMb: 0,
        showOptionalDocumentsSection: true,
        sections,
      },
    ],
    [sectionStepId, nameEn, nameFr, sections],
  );

  const handleSaveFieldConditions = useCallback(
    async (
      sectionId: string,
      rowId: string,
      columnId: string,
      conditions: FileEntryCondition[],
      combinator: 'all' | 'any' | null,
    ) => {
      setSections((prev) =>
        prev.map((sec) =>
          sec.id !== sectionId
            ? sec
            : {
                ...sec,
                rows: sec.rows.map((row) =>
                  row.id !== rowId
                    ? row
                    : {
                        ...row,
                        columns: row.columns.map((col) =>
                          col.id !== columnId
                            ? col
                            : {
                                ...col,
                                field: col.field
                                  ? { ...col.field, conditions, conditionCombinator: combinator }
                                  : col.field,
                              },
                        ),
                      },
                ),
              },
        ),
      );
    },
    [],
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await customElementsApi.updateSection(id, nameEn, nameFr, sectionToPayload(sections[0]!));
      void message.success(t('workflow.customElements.saved'));
    } catch (err) {
      handleApiError(err, t, t('workflow.customElements.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 600,
        padding: '0 16px',
      }}
    >
      <PageHeader
        title={t('workflow.customElements.editSection')}
        onBack={onBack}
        actions={
          <Button type="primary" loading={saving} onClick={() => void handleSave()}>
            {t('common.save')}
          </Button>
        }
      />
      <NameFields
        nameEn={nameEn}
        nameFr={nameFr}
        onChangeNameEn={setNameEn}
        onChangeNameFr={setNameFr}
      />
      <div style={{ marginTop: 12, flex: 1, minHeight: 0 }}>
        <DynamicFormEditorShell
          sections={sections}
          dataSource={dataSource}
          templateId={undefined}
          stepId={sectionStepId}
          allSteps={syntheticAllSteps}
          onSaveFieldConditions={handleSaveFieldConditions}
          singleSectionMode
          maxHeight="75vh"
        />
      </div>
    </div>
  );
}

function StepElementEditor({
  id,
  element,
  onBack,
}: {
  id: string;
  element: CustomElement;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const [nameEn, setNameEn] = useState(element.nameEn);
  const [nameFr, setNameFr] = useState(element.nameFr);
  const [sections, setSections] = useState<FormSection[]>(element.step!.sections);
  const [stepDetails, setStepDetails] = useState({
    titleEn: element.step!.stepTitleEn,
    titleFr: element.step!.stepTitleFr,
    descriptionEn: element.step!.stepDescriptionEn,
    descriptionFr: element.step!.stepDescriptionFr,
  });
  const [stepDetailsOpen, setStepDetailsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Use the custom element's own (route-stable) id as the synthetic step id — sent
  // to the backend as SourceStepId on every save, so it must stay identical across
  // sessions or ConditionStripper.StripStep will treat existing conditions as
  // pointing at a different step and strip them.
  const sourceStepId = id;

  const dataSource = useMemo(() => createInMemoryFormEditorDataSource(setSections), []);

  const syntheticAllSteps = useMemo(
    (): WorkflowStep[] => [
      {
        id: sourceStepId,
        type: 'DynamicForm',
        titleEn: stepDetails.titleEn,
        titleFr: stepDetails.titleFr,
        descriptionEn: stepDetails.descriptionEn,
        descriptionFr: stepDetails.descriptionFr,
        fileEntries: [],
        additionalDocumentEntries: [],
        optionalDocumentExtensions: [],
        optionalDocumentMaxFileSizeMb: 0,
        showOptionalDocumentsSection: true,
        sections,
      },
    ],
    [sourceStepId, stepDetails, sections],
  );

  const handleSaveFieldConditions = useCallback(
    async (
      sectionId: string,
      rowId: string,
      columnId: string,
      conditions: FileEntryCondition[],
      combinator: 'all' | 'any' | null,
    ) => {
      setSections((prev) =>
        prev.map((sec) =>
          sec.id !== sectionId
            ? sec
            : {
                ...sec,
                rows: sec.rows.map((row) =>
                  row.id !== rowId
                    ? row
                    : {
                        ...row,
                        columns: row.columns.map((col) =>
                          col.id !== columnId
                            ? col
                            : {
                                ...col,
                                field: col.field
                                  ? { ...col.field, conditions, conditionCombinator: combinator }
                                  : col.field,
                              },
                        ),
                      },
                ),
              },
        ),
      );
    },
    [],
  );

  const handleSaveSectionConditions = useCallback(
    async (
      sectionId: string,
      conditions: FileEntryCondition[],
      combinator: 'all' | 'any' | null,
    ) => {
      setSections((prev) =>
        prev.map((sec) =>
          sec.id !== sectionId ? sec : { ...sec, conditions, conditionCombinator: combinator },
        ),
      );
    },
    [],
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await customElementsApi.updateStep(id, nameEn, nameFr, sourceStepId, {
        stepTitleEn: stepDetails.titleEn,
        stepTitleFr: stepDetails.titleFr,
        stepDescriptionEn: stepDetails.descriptionEn,
        stepDescriptionFr: stepDetails.descriptionFr,
        sections: sections.map(sectionToPayload),
      });
      void message.success(t('workflow.customElements.saved'));
    } catch (err) {
      handleApiError(err, t, t('workflow.customElements.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 600,
        padding: '0 16px',
      }}
    >
      <PageHeader
        title={t('workflow.customElements.editStep')}
        onBack={onBack}
        actions={
          <Button type="primary" loading={saving} onClick={() => void handleSave()}>
            {t('common.save')}
          </Button>
        }
      />
      <NameFields
        nameEn={nameEn}
        nameFr={nameFr}
        onChangeNameEn={setNameEn}
        onChangeNameFr={setNameFr}
      />
      <div style={{ marginTop: 12, flex: 1, minHeight: 0 }}>
        <DynamicFormEditorShell
          sections={sections}
          dataSource={dataSource}
          templateId={undefined}
          stepId={sourceStepId}
          allSteps={syntheticAllSteps}
          onSaveFieldConditions={handleSaveFieldConditions}
          onSaveSectionConditions={handleSaveSectionConditions}
          onEditStepDetails={() => setStepDetailsOpen(true)}
          maxHeight="75vh"
        />
      </div>
      <CustomStepDetailsModal
        open={stepDetailsOpen}
        initial={stepDetails}
        onCancel={() => setStepDetailsOpen(false)}
        onSubmit={(values) => {
          setStepDetails(values);
          setStepDetailsOpen(false);
        }}
      />
    </div>
  );
}
